/**
 * Supabase Client Configuration
 *
 * CRITICAL RULES:
 * 1. This app is READ-ONLY - no auth, no writes
 * 2. Environment validation is MANDATORY before client creation
 * 3. NEVER use hostname-based connection switching
 * 4. Connection URL comes from environment variables ONLY
 */

import { createClient } from '@supabase/supabase-js';
import { assertValidEnvironment, type EnvironmentInfo } from './env-validator';
import type { Database } from './supabase.types';

// Validate environment before creating client
let envInfo: EnvironmentInfo;

try {
  envInfo = assertValidEnvironment();
} catch (error) {
  console.error('❌ Environment validation failed:', error);
  throw error;
}

// Get Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate required environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing required environment variables:\n' +
    `VITE_SUPABASE_URL: ${supabaseUrl ? '✓' : '✗'}\n` +
    `VITE_SUPABASE_ANON_KEY: ${supabaseAnonKey ? '✓' : '✗'}\n\n` +
    'Please check your .env file or Vercel environment variables.'
  );
}

// Create Supabase client
// Note: No auth configuration as this app is read-only
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

// Export environment info for use in components
export { envInfo };

// Log successful initialization
if (import.meta.env.VITE_ENABLE_CONSOLE_LOG === 'true') {
  console.log('✅ Supabase client initialized successfully');
  console.log(`   Environment: ${envInfo.environment}`);
  console.log(`   Project Ref: ${envInfo.projectRef}`);
}
